import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import PhoneIcon from "@mui/icons-material/Phone";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { callQueueEntry, finishQueueEntry, getQueue, updateQueueSettings } from "apis/queueAPIs";
import { showErrorSnackbar, showSuccessSnackbar } from "store/actions/snackbarMessage";
import { minutesWaited, queueRefLabel } from "./queueFormUtils";
import QueueSmsSettings from "./QueueSmsSettings";

const QUEUE_KEY = ["merchantQueue"];
// The portal has no Firestore client, so the list polls.
const REFRESH_MS = 15000;

const QueueEntry = ({ entry, position, onCall, onSeat, onRemove, busy }) => {
  const waited = minutesWaited(entry.createdAt);
  const called = entry.status === "called";
  const sinceCalled = called ? minutesWaited(entry.calledAt) : null;
  return (
    <Card variant="outlined">
      <CardContent sx={{ "&:last-child": { pb: 2 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h3" component="span" sx={{ minWidth: 40, color: "text.secondary" }}>
              {position}
            </Typography>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="h5" component="span" noWrap>
                  {entry.name}
                </Typography>
                <Chip size="small" label={queueRefLabel(entry)} sx={{ fontWeight: 700 }} />
                {called ? (
                  <Chip
                    size="small"
                    color="success"
                    label={sinceCalled ? `Called ${sinceCalled} min ago` : "Called"}
                  />
                ) : null}
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PeopleIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {entry.guestNum} {Number(entry.guestNum) === 1 ? "guest" : "guests"}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PhoneIcon fontSize="small" color="action" />
                  <Link variant="body2" href={`tel:${entry.phoneNumber}`}>
                    {entry.phoneNumber}
                  </Link>
                </Stack>
                {waited !== null ? (
                  <Typography variant="body2" color="text.secondary">
                    Waiting {waited} min
                  </Typography>
                ) : null}
              </Stack>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant={called ? "outlined" : "contained"}
              color="success"
              onClick={() => onCall(entry)}
              disabled={busy}
              sx={{ flex: { xs: 1, sm: "none" }, whiteSpace: "nowrap" }}
            >
              {called ? "Call again" : "Call"}
            </Button>
            <Button variant="contained" onClick={() => onSeat(entry)} disabled={busy} sx={{ flex: { xs: 1, sm: "none" } }}>
              Seat
            </Button>
            <Button variant="outlined" color="error" onClick={() => onRemove(entry)} disabled={busy} sx={{ flex: { xs: 1, sm: "none" } }}>
              Remove
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

QueueEntry.propTypes = {
  entry: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    guestNum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    phoneNumber: PropTypes.string,
    createdAt: PropTypes.number,
    calledAt: PropTypes.number,
    status: PropTypes.string,
    queueNumber: PropTypes.number,
  }).isRequired,
  position: PropTypes.number.isRequired,
  onCall: PropTypes.func.isRequired,
  onSeat: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  busy: PropTypes.bool,
};

const QueueList = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState(null);
  const [waitInput, setWaitInput] = useState("");
  // Re-render each minute so "Waiting N min" stays current between polls.
  const [, setTick] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: QUEUE_KEY,
    queryFn: getQueue,
    refetchInterval: REFRESH_MS,
    staleTime: 0,
  });

  const savedWaitingMinutes = data ? data.waitingMinutes || 0 : null;
  useEffect(() => {
    if (savedWaitingMinutes !== null) setWaitInput(String(savedWaitingMinutes));
  }, [savedWaitingMinutes]);

  const finishMutation = useMutation({
    mutationFn: ({ entry, status }) => finishQueueEntry(entry.id, status),
    onSuccess: (_, { entry, status }) => {
      dispatch(showSuccessSnackbar(`${entry.name} ${status === "seated" ? "seated" : "removed"}`));
      queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
    onError: (err) => {
      console.error("finishQueueEntry failed", err);
      dispatch(showErrorSnackbar("Could not update the queue, please try again"));
      queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });

  const callMutation = useMutation({
    mutationFn: (entry) => callQueueEntry(entry.id),
    onSuccess: (_, entry) => {
      dispatch(showSuccessSnackbar(`${queueRefLabel(entry)} ${entry.name} has been called`));
      queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
    onError: (err) => {
      console.error("callQueueEntry failed", err);
      dispatch(showErrorSnackbar("Could not call the guest, please try again"));
    },
  });

  const settingsMutation = useMutation({
    mutationFn: updateQueueSettings,
    onSuccess: (_, settings) => {
      dispatch(
        showSuccessSnackbar(
          settings.smsTemplate === undefined ? "Wait time saved" : settings.smsTemplate ? "SMS message saved" : "SMS turned off"
        )
      );
      queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
    onError: (err, settings) => {
      console.error("updateQueueSettings failed", err);
      dispatch(
        showErrorSnackbar(
          `Could not save the ${settings.smsTemplate === undefined ? "wait time" : "SMS message"}, please try again`
        )
      );
    },
  });

  const forbidden = error && error.response && error.response.status === 403;

  const entries = (data && data.entries) || [];
  const waitValue = Number(waitInput);
  const waitValid = waitInput !== "" && Number.isInteger(waitValue) && waitValue >= 0 && waitValue <= 600;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4">Queue</Typography>
          <Typography variant="body2" color="text.secondary">
            {entries.length} {entries.length === 1 ? "party" : "parties"} waiting ·{" "}
            {entries.reduce((sum, e) => sum + (Number(e.guestNum) || 0), 0)} guests
          </Typography>
        </Box>
        <Button onClick={() => refetch()} startIcon={<RefreshIcon />}>
          Refresh
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, maxWidth: 420 }}>
        <TextField
          label="Estimated wait (minutes)"
          type="number"
          value={waitInput}
          onChange={(e) => setWaitInput(e.target.value)}
          error={!waitValid}
          helperText="Shown to guests when they join. 0 hides it."
          inputProps={{ min: 0, max: 600, step: 1, inputMode: "numeric" }}
          sx={{ minWidth: "0 !important", flex: 1 }}
        />
        <Button
          variant="outlined"
          onClick={() => settingsMutation.mutate({ waitingMinutes: waitValue })}
          disabled={!waitValid || settingsMutation.isPending || waitValue === savedWaitingMinutes}
          sx={{ height: 56, flexShrink: 0, mb: "11px !important" }}
        >
          Save
        </Button>
      </Stack>

      {data ? (
        <QueueSmsSettings
          savedTemplate={data.smsTemplate || ""}
          saving={settingsMutation.isPending}
          onSave={(smsTemplate) => settingsMutation.mutate({ smsTemplate })}
        />
      ) : null}

      {isLoading || isFetching ? <LinearProgress sx={{ mb: 2 }} /> : <Box sx={{ height: 4, mb: 2 }} />}

      {forbidden ? (
        <Typography color="error">
          You don&rsquo;t have access to the queue. Ask a restaurant admin to give you the Queue management permission.
        </Typography>
      ) : error ? (
        <Typography color="error">Could not load the queue. It will retry automatically.</Typography>
      ) : null}

      {!isLoading && !error && entries.length === 0 ? (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: "center", py: 6 }}>
            <Typography variant="h6">No one is waiting</Typography>
            <Typography variant="body2" color="text.secondary">
              Guests who join from the queue QR code will show up here.
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      <Stack spacing={1.5}>
        {entries.map((entry, index) => (
          <QueueEntry
            key={entry.id}
            entry={entry}
            position={index + 1}
            busy={finishMutation.isPending || callMutation.isPending}
            onCall={(e) => callMutation.mutate(e)}
            onSeat={(e) => finishMutation.mutate({ entry: e, status: "seated" })}
            onRemove={setRemoving}
          />
        ))}
      </Stack>

      <Dialog open={Boolean(removing)} onClose={() => setRemoving(null)}>
        <DialogTitle>Remove from queue?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {removing ? `${removing.name} (${removing.guestNum} guests) will be marked as a no-show.` : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoving(null)}>Keep</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              finishMutation.mutate({ entry: removing, status: "cancelled" });
              setRemoving(null);
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueueList;
